import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompleteJobInfoForm } from '../../../hooks/useCompleteJobInfoForm';
import {
  JobOffer,
  MatchingPriorities,
  Skill,
} from '../../../models/job';
import { Dialog } from '@/components/ui/organisms/Dialog';
import { LoadingPanel } from './LoadingPanel';
import {
  JobFlowModalFooterActions,
  jobFlowDialogBodyClassName,
  jobFlowDialogFooterClassName,
} from './jobFlowModalChrome';
import {
  JobRefineSectionFields,
  JobRefineSectionHeader,
  REFINE_SECTIONS,
  RefineSectionKey,
} from './jobRefineSections';

interface ReviewStateProps {
  jobId: string;
  job: JobOffer | null;
  missingFields: string[];
  criticalMissingFields: string[];
  isOpen: boolean;
  onComplete: () => void;
}

export const ReviewState: React.FC<ReviewStateProps> = ({
  jobId,
  job,
  missingFields,
  criticalMissingFields,
  isOpen,
  onComplete,
}) => {
  const { t } = useTranslation('dashboard');
  const submitTips = [
    t('create_job_modal.submit_tip_1'),
    t('create_job_modal.submit_tip_2'),
    t('create_job_modal.submit_tip_3'),
  ];
  const form = useCompleteJobInfoForm({
    jobId,
    missingFields,
    criticalMissingFields,
    isOpen,
    job,
  });

  // Skip the hook's intro step exactly once. Without this guard, React Strict Mode
  // double-invokes the effect and we jump straight to Skills.
  const skippedIntroRef = useRef(false);
  useEffect(() => {
    if (!skippedIntroRef.current && form.step === 0) {
      skippedIntroRef.current = true;
      form.handleNextStep();
    }
  }, [form.step, form]);

  useEffect(() => {
    if (form.phase === 'complete') onComplete();
  }, [form.phase, onComplete]);

  // Hook steps 1..4 → refine sections 0..3.
  const sectionIdx = Math.max(0, form.step - 1);
  const section = REFINE_SECTIONS[Math.min(sectionIdx, REFINE_SECTIONS.length - 1)];
  const isLast = sectionIdx >= REFINE_SECTIONS.length - 1;
  const isSubmitting = form.isSubmitting || form.phase === 'loading';

  const handleBack = () => {
    if (sectionIdx > 0) form.handlePrevStep();
  };

  const handleContinue = () => {
    if (isLast) form.handleSubmit();
    else form.handleNextStep();
  };

  if (isSubmitting) {
    return (
      <Dialog.Body className={jobFlowDialogBodyClassName}>
        <LoadingPanel title={t('create_job_modal.almost_there')} tips={submitTips} />
      </Dialog.Body>
    );
  }

  return (
    <>
      <Dialog.Body className={jobFlowDialogBodyClassName}>
        <div className="shrink-0">
          <JobRefineSectionHeader sections={REFINE_SECTIONS} sectionIdx={sectionIdx} />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <JobRefineSectionFields
            sectionKey={section.key as RefineSectionKey}
            getValue={(fieldName) => form.formData[fieldName]}
            onChange={(fieldName, value) =>
              form.updateFormData(fieldName, value as string | Skill[] | MatchingPriorities)
            }
            defaultMatchingPriorities={job?.matchingPriorities}
          />
        </div>
      </Dialog.Body>

      <Dialog.Footer className={jobFlowDialogFooterClassName}>
        <JobFlowModalFooterActions
          backLabel={t('create_job_modal.back')}
          primaryLabel={
            isLast ? t('create_job_modal.save_continue') : t('create_job_modal.continue')
          }
          onBack={handleBack}
          onPrimary={handleContinue}
          backDisabled={sectionIdx === 0}
        />
      </Dialog.Footer>
    </>
  );
};
